/**
 * Что зритель выбрал видеть в оболочке приложения: свёрнуто ли левое меню
 * и в каком разделе он находится. Тема оформления живёт рядом, в `theme.ts`.
 *
 * Как и настройки карты, это предпочтение того, кто смотрит, а не свойство
 * организации: с ноутбука меню держат раскрытым, с телефона — свёрнутым,
 * и общего правильного ответа тут нет. Отсюда и хранилище: браузер, а не база.
 *
 * Ключи без пользователя и без организации: полоса меню — свойство этого
 * экрана, и после смены учётной записи на том же ноутбуке ширина должна
 * остаться прежней. Раздел от чужих прав защищён не ключом, а проверкой
 * при восстановлении: недоступный раздел заменяется доступным.
 */

import { viewportOf } from './viewport';

const NAV_COLLAPSED_KEY = 'netdeck.shell.nav-collapsed';

const SECTION_KEY = 'netdeck.shell.section';

/**
 * Свёрнуто ли меню.
 *
 * Пока человек не трогал переключатель, отвечает ширина экрана: на телефоне
 * раскрытое меню закрывает собой карту, ради которой на него и зашли, а на
 * ноутбуке свёрнутое прятало бы названия разделов без всякой причины.
 */
export function readNavCollapsed(): boolean {
  try {
    const stored = localStorage.getItem(NAV_COLLAPSED_KEY);

    if (stored !== null) {
      return stored === 'on';
    }
  } catch {
    // Хранилище недоступно — приватный режим, запрет сторонних данных.
    // Решает ширина экрана: она есть всегда.
  }

  // Меню в 200 px съедает у планшета треть ширины, у телефона половину:
  // развёрнутым оно начинается только на десктопе (см. `viewport`).
  return viewportOf(window.innerWidth) !== 'desktop';
}

export function writeNavCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(NAV_COLLAPSED_KEY, collapsed ? 'on' : 'off');
  } catch {
    // Не сохранилось — переживём: на текущем экране меню уже свёрнуто.
  }
}

/**
 * Раздел, открытый в прошлый раз.
 *
 * Перезагрузка страницы — не начало рабочего дня, а его продолжение: она
 * случается от F5, от восстановления сессии, от обновления вкладки после
 * простоя, и возвращать человека из архива видео на «Площадки» каждый такой
 * раз значит терять его место в работе. Возвращается сырая строка: годен ли
 * раздел при нынешних правах, решает `navigation.resolveSection`, потому что
 * права здесь неизвестны.
 */
export function readSection(): string | null {
  try {
    return localStorage.getItem(SECTION_KEY);
  } catch {
    // Хранилище недоступно — откроется раздел по умолчанию.
    return null;
  }
}

export function writeSection(id: string): void {
  try {
    localStorage.setItem(SECTION_KEY, id);
  } catch {
    // Не сохранилось — переживём: раздел уже открыт, потеряется только
    // память о нём к следующей загрузке.
  }
}
